/**
 * The chart ledger (M3 spec §6.2; plan R5): chart value accrues per system while unsold and moves into `soldTotal`
 * when sold. Valuation (base × novelty × richness, first-discovery bonus) is R18's; this only keeps the books.
 */
import type { ReachState } from '../types'

/** Adds `amount` (ignored unless positive and finite) to the system's unsold chart value. Returns the new unsold value. */
export function accrueChart(state: ReachState, systemId: string, amount: number): number {
  const current = state.charts.unsold[systemId] ?? 0
  if (!(amount > 0) || !Number.isFinite(amount)) return current
  const next = current + amount
  state.charts.unsold[systemId] = next
  return next
}

/** Every system with unsold chart value, as [systemId, value] pairs in insertion order. */
export function unsoldCharts(state: ReachState): [string, number][] {
  return Object.entries(state.charts.unsold).filter(([, v]) => v > 0)
}

/** Total unsold chart value. */
export function unsoldTotal(state: ReachState): number {
  let total = 0
  for (const v of Object.values(state.charts.unsold)) total += v
  return total
}

/** Sells every unsold chart: moves the values into `soldTotal`, zeroes `unsold` and returns the credits earned. */
export function sellAllCharts(state: ReachState): number {
  let credits = 0
  for (const [systemId, value] of Object.entries(state.charts.unsold)) {
    if (value > 0) {
      credits += value
      state.charts.soldTotal[systemId] = (state.charts.soldTotal[systemId] ?? 0) + value
    }
  }
  state.charts.unsold = {}
  return credits
}

/** Records a first discovery (the ×2 bonus is applied by the valuation). True the first time. */
export function markFirstDiscovery(state: ReachState, systemId: string): boolean {
  if (state.charts.firstDiscoveries.includes(systemId)) return false
  state.charts.firstDiscoveries.push(systemId)
  return true
}
